import { useState, type DragEvent } from 'react'
import type { MidiItem } from './midiState'

type Props = {
  items: MidiItem[]
  playingId: string | null
  onRemove: (id: string) => void
  onReorder: (from: number, to: number) => void
  onPlayToggle: (id: string) => void
  onInsertFiles: (targetIndex: number, files: File[]) => void
}

function downloadItem(sourceName: string, bytes: Uint8Array) {
  const url = URL.createObjectURL(
    new Blob([bytes as BlobPart], { type: 'audio/midi' }),
  )
  const a = document.createElement('a')
  a.href = url
  a.download = sourceName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function MidiList({
  items,
  playingId,
  onRemove,
  onReorder,
  onPlayToggle,
  onInsertFiles,
}: Props) {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  if (items.length === 0) {
    return (
      <p className="music-player__list-empty">
        No MIDI files yet. Drop <b>.mid</b> files above to add them.
      </p>
    )
  }

  const handleDragStart =
    (index: number) => (e: DragEvent<HTMLLIElement>) => {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
      setDraggingIndex(index)
    }

  const handleDragOver =
    (index: number) => (e: DragEvent<HTMLLIElement>) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = e.dataTransfer.types.includes('Files')
        ? 'copy'
        : 'move'
      if (dragOverIndex !== index) setDragOverIndex(index)
    }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop =
    (targetIndex: number) => (e: DragEvent<HTMLLIElement>) => {
      e.preventDefault()
      setDraggingIndex(null)
      setDragOverIndex(null)

      if (e.dataTransfer.files.length > 0) {
        e.stopPropagation()
        onInsertFiles(targetIndex, Array.from(e.dataTransfer.files))
        return
      }

      const raw = e.dataTransfer.getData('text/plain')
      const sourceIndex = Number.parseInt(raw, 10)
      if (!Number.isFinite(sourceIndex)) return
      if (sourceIndex === targetIndex) return
      onReorder(sourceIndex, targetIndex)
    }

  const handleDragEnd = () => {
    setDraggingIndex(null)
    setDragOverIndex(null)
  }

  return (
    <ol className="music-player__list">
      {items.map((item, index) => {
        const classes = [
          'music-player__list-item',
          draggingIndex === index ? 'music-player__list-item--dragging' : '',
          dragOverIndex === index && draggingIndex !== index
            ? 'music-player__list-item--dragover'
            : '',
        ]
          .filter(Boolean)
          .join(' ')
        const targetName = `${String(index + 1).padStart(4, '0')}.mid`
        const isPlaying = playingId === item.id
        return (
          <li
            key={item.id}
            className={classes}
            draggable
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver(index)}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop(index)}
            onDragEnd={handleDragEnd}
          >
            <span
              className="music-player__list-handle"
              aria-hidden="true"
              title="Drag to reorder"
            >
              ⋮⋮
            </span>
            <span className="music-player__list-target" aria-label="Output filename">
              {targetName}
            </span>
            <span
              className="music-player__list-name"
              title={item.sourceName}
            >
              {item.sourceName}
            </span>
            {item.status === 'parsing' && (
              <span className="music-player__list-chip music-player__list-chip--info">
                parsing…
              </span>
            )}
            {item.status === 'ready' && (
              <span className="music-player__list-chip">
                {item.summary.nIn} → {item.summary.nOut} tracks
              </span>
            )}
            {item.status === 'error' && (
              <span
                className="music-player__list-chip music-player__list-chip--err"
                title={item.message}
              >
                {item.message}
              </span>
            )}
            {item.status === 'ready' && (
              <button
                type="button"
                className="music-player__list-btn"
                onClick={() => onPlayToggle(item.id)}
                aria-label={`${isPlaying ? 'Pause' : 'Play'} ${item.sourceName}`}
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
            )}
            {item.status === 'ready' && (
              <button
                type="button"
                className="music-player__list-btn"
                onClick={() => downloadItem(item.sourceName, item.bytes)}
                aria-label={`Download simplified ${item.sourceName}`}
                title="Download simplified .mid"
              >
                📥
              </button>
            )}
            <button
              type="button"
              className="music-player__list-btn"
              onClick={() => onRemove(item.id)}
              aria-label={`Remove ${item.sourceName}`}
              title="Remove"
            >
              ✕
            </button>
          </li>
        )
      })}
    </ol>
  )
}
